import React, { useEffect, useState } from 'react';
import { Modal, Radio, Button, Space, Select, message } from 'antd';
import { DownloadOutlined } from '@ant-design/icons';
import { BillExportTemplateType } from '../../graphql/types/settlement';
import type { SettlementBillType } from '../../graphql/types/settlement';
import { formatTimeWithTimezone } from '../../utils/timezoneFormatter';
import { getBillTypeLabel } from '../../utils/billI18n';

const { Option } = Select;

export type ExportLanguage = 'EN' | 'ZH' | 'ES';

const LANGUAGE_OPTIONS: { value: ExportLanguage; label: string }[] = [
  { value: 'ZH', label: '中文' },
  { value: 'EN', label: 'English' },
  { value: 'ES', label: 'Español' },
];

interface BillExportModalProps {
  visible: boolean;
  bill: SettlementBillType | null;
  onCancel: () => void;
  onExport: (template: BillExportTemplateType, language: ExportLanguage) => Promise<void>;
}

/**
 * 账单导出模态框
 * 支持不同模板的导出选择；DSP运费模板支持多语言表头
 */
export const BillExportModal: React.FC<BillExportModalProps> = ({
  visible,
  bill,
  onCancel,
  onExport
}) => {
  const [selectedTemplate, setSelectedTemplate] = useState<BillExportTemplateType>(BillExportTemplateType.GENERAL_TEMPLATE);
  const [selectedLanguage, setSelectedLanguage] = useState<ExportLanguage>('ZH');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!bill) return;
    if (bill.billType === 'SLA_BILL') {
      setSelectedTemplate(BillExportTemplateType.SLA_TEMPLATE);
      return;
    }
    if (bill.billType === 'DSP_DELIVERY_FEE_BILL') {
      setSelectedTemplate(BillExportTemplateType.DSP_DELIVERY_TEMPLATE);
      return;
    }
    if (bill.billType === 'SORTING_BILL') {
      setSelectedTemplate(BillExportTemplateType.TT_SORTING_TEMPLATE);
      return;
    }
    setSelectedTemplate(BillExportTemplateType.GENERAL_TEMPLATE);
  }, [bill]);

  const handleExport = async () => {
    if (!bill) return;

    setLoading(true);
    try {
      await onExport(selectedTemplate, selectedLanguage);
    } catch (error) {
      message.error('导出失败: ' + (error as Error).message);
    } finally {
      setLoading(false);
    }
  };

  const getTemplateOptions = () => {
    if (!bill) return [];

    const options = [];

    if (bill.billType === 'DELIVERY_FEE_BILL') {
      options.push(
        { value: BillExportTemplateType.TEMU_TEMPLATE, label: 'Temu模板', description: '包含Temu特有的费用项和格式' },
        { value: BillExportTemplateType.TEMU_HALF_MANAGED_TEMPLATE, label: 'Temu半托管模板', description: 'Temu半托管账单特有的格式和计算逻辑' },
        { value: BillExportTemplateType.GENERAL_TEMPLATE, label: '一般客户模板', description: '标准配送费账单格式' },
        { value: BillExportTemplateType.CUSTOMER_STANDARD_TEMPLATE, label: '客户标准模板', description: '费用列按主费用项+子费用项汇总展示' },
        { value: BillExportTemplateType.TIKTOK_TEMPLATE, label: 'TikTok客户模板', description: 'TikTok客户定制账单格式' },
        { value: BillExportTemplateType.JD_TEMPLATE, label: '京东客户模板', description: '京东客户定制账单格式' }
      );
    }

    if (bill.billType === 'DSP_DELIVERY_FEE_BILL') {
      options.push(
        { value: BillExportTemplateType.DSP_DELIVERY_TEMPLATE, label: 'DSP运费模板', description: '生成【客户名】配送_【账期】_Summary.xlsx 与 _Detail.xlsx 两个文件' }
      );
    }

    if (bill.billType === 'SLA_BILL') {
      options.push(
        { value: BillExportTemplateType.SLA_TEMPLATE, label: 'SLA模板', description: 'SLA考核费用账单格式' }
      );
    }

    if (bill.billType === 'SORTING_BILL') {
      options.push(
        { value: BillExportTemplateType.TT_SORTING_TEMPLATE, label: 'TT分拣模板', description: '生成【客户名】分拣费_【账期】_Summary.xlsx 与 _Detail.xlsx 两个文件' }
      );
    }

    return options;
  };

  const templateOptions = getTemplateOptions();
  const showLanguageSelector = selectedTemplate === BillExportTemplateType.DSP_DELIVERY_TEMPLATE;

  return (
    <Modal
      title="导出账单"
      open={visible}
      onCancel={onCancel}
      footer={[
        <Button key="cancel" onClick={onCancel}>
          取消
        </Button>,
        <Button
          key="export"
          type="primary"
          icon={<DownloadOutlined />}
          loading={loading}
          onClick={handleExport}
        >
          导出
        </Button>
      ]}
      width={600}
    >
      {bill && (
        <div className="space-y-4">
          {/* 账单信息 */}
          <div className="bg-gray-50 p-4 rounded">
            <h4 className="font-medium mb-2">账单信息</h4>
            <div className="grid grid-cols-2 gap-2 text-sm">
              <div>
                <span className="text-gray-600">账单ID:</span>
                <span className="ml-2 font-mono">{bill.id}</span>
              </div>
              <div>
                <span className="text-gray-600">客户名称:</span>
                <span className="ml-2">{bill.clientName}</span>
              </div>
              <div>
                <span className="text-gray-600">账单类型:</span>
                <span className="ml-2">
                  {getBillTypeLabel(bill.billType)}
                </span>
              </div>
              <div>
                <span className="text-gray-600">账单金额:</span>
                <span className="ml-2 font-medium">{bill.billAmount}</span>
              </div>
              <div className="col-span-2">
                <span className="text-gray-600">账单周期:</span>
                <span className="ml-2">
                  {formatTimeWithTimezone(bill.periodStart, bill.timeZone)} ~ {formatTimeWithTimezone(bill.periodEnd, bill.timeZone)}
                </span>
                <div className="text-xs text-gray-500 mt-1">
                  ({bill.timeZone})
                </div>
              </div>
            </div>
          </div>

          {/* 模板选择 */}
          <div>
            <h4 className="font-medium mb-3">选择导出模板</h4>
            <Radio.Group
              value={selectedTemplate}
              onChange={(e) => setSelectedTemplate(e.target.value)}
              className="w-full"
            >
              <Space direction="vertical" className="w-full">
                {templateOptions.map(option => (
                  <Radio key={option.value} value={option.value} className="w-full">
                    <div className="ml-2">
                      <div className="font-medium">{option.label}</div>
                      <div className="text-sm text-gray-600">{option.description}</div>
                    </div>
                  </Radio>
                ))}
              </Space>
            </Radio.Group>
          </div>

          {/* 语言选择（仅 DSP 运费模板） */}
          {showLanguageSelector && (
            <div>
              <h4 className="font-medium mb-2">选择导出语言</h4>
              <Select
                value={selectedLanguage}
                onChange={(val) => setSelectedLanguage(val)}
                className="w-48"
              >
                {LANGUAGE_OPTIONS.map(opt => (
                  <Option key={opt.value} value={opt.value}>{opt.label}</Option>
                ))}
              </Select>
              <p className="text-xs text-gray-500 mt-1">
                影响 DSP Summary 附件及明细文件的表头语言
              </p>
            </div>
          )}
        </div>
      )}
    </Modal>
  );
};
